import { v3 } from "backoffice-api"
import { useQueryAll } from "bonzai"
import { sortBy } from "lodash-es"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { QueryBoundary } from "utility-components"
import { custom } from "../../../bonzai/bonzai"
import { toggleActivityItem } from "../../../bonzai/toggleActivityItem"
import { createPlaythrough } from "../../../graphql/createPlaythrough"
import { usePickText } from "../../../i18n/usePickText"
import {
  pollForProgressChange,
  snapshotProgressBefore,
} from "../../ConfettiBanner/checkForProgressChange"
import { ProductModalActivityAttachments } from "../ProductModalActivityAttachments"
import { ProductModalActivity } from "./ProductModalActivity"

type ProductModalActivityLoaderProps = {
  productId: number
  onClose: () => void
}
export const ProductModalActivityLoader = (
  props: ProductModalActivityLoaderProps
) => (
  <QueryBoundary fallback={<ProductModalActivity.Skeleton />}>
    <Load {...props} />
  </QueryBoundary>
)

const Load = ({ productId, onClose }: ProductModalActivityLoaderProps) => {
  const pickText = usePickText()
  const { t } = useTranslation()
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [status, product, tasks] = useData(productId)

  const selectedTask = tasks.find((task) => task.id === selectedTaskId)

  // Snapshot the product's progress when the activity opens (before the user
  // confirms) so the completion poll can diff against it. No extra network call
  // — the activity query already carries this progress.
  //
  // The poll's `after` comes from the graphql CurrentProductProgress query
  // (certified/highScore/starsCount). We seed `before` from the v1 REST progress
  // this view already has; the values are equivalent — all three read from the
  // same backend model (certified: boolean, high_score/highScore and
  // stars/starsCount: plain integers, no scale/rounding difference), only the
  // field names differ. The activity view has no graphql progress to snapshot
  // from without an extra query, so we map the REST names here.
  useEffect(() => {
    snapshotProgressBefore(String(productId), {
      certified: status.certified,
      highScore: status.high_score,
      starsCount: status.stars,
    })
  }, [productId, status])

  const isCertified = status.certified
  const numberOfTasks = tasks.length
  const numberOfCompletedTasks = tasks.filter((item) => item.done).length
  const completedIfCertified = isCertified
    ? numberOfTasks
    : numberOfCompletedTasks

  const isLocked = status.stars > 0
  const isBottomVisible = numberOfCompletedTasks >= numberOfTasks && !isLocked

  const taskElements = tasks.map((task) => (
    <ProductModalActivity.Task
      key={task.id}
      title={pickText(task.title)}
      isChecked={task.done}
      isLocked={isLocked}
      isRejected={task.rejected}
      hasAddedAttachments={
        task.photo_url !== null || (task.note !== null && task.note !== "")
      }
      showAttachmentButton={task.activity_attachments}
      onClick={() => toggleActivityItem(productId, task)}
      onAttachmentsClick={() => setSelectedTaskId(task.id)}
    />
  ))

  const onConfirm = () => {
    onClose()
    createPlaythrough(String(productId))
    // Unlike ViewerModal.handleClose (which invalidates unconditionally because a
    // plain close should reflect any background state change), a confirm always
    // creates a playthrough, so the poll below is the sole refresh trigger — it
    // invalidates once it detects the backend's processed progress. Single-flight
    // in the poll module keeps this safe if completion fires twice.
    pollForProgressChange(String(productId))
  }

  const getContent = () => {
    if (selectedTask) {
      return (
        <ProductModalActivityAttachments
          productId={productId}
          activityItemId={selectedTask.id}
          taskTitle={pickText(selectedTask.title)}
          isRejected={selectedTask.rejected}
          taskNote={selectedTask.note}
          taskPhotoUrl={selectedTask.photo_url}
          isLocked={isLocked}
          onBack={() => setSelectedTaskId(null)}
        />
      )
    } else {
      return (
        <>
          <ProductModalActivity.Top
            isLocked={isLocked}
            completedCount={completedIfCertified}
            totalCount={numberOfTasks}
          />
          <ProductModalActivity.Tasks>
            {taskElements}
          </ProductModalActivity.Tasks>
          <ProductModalActivity.Bottom isVisible={isBottomVisible}>
            <ProductModalActivity.WaitingForApproval
              isVisible={product.manager_approval}
              title={t("activity.PENDING_APPROVAL")}
              tooltipTitle={t("activity.PENDING_APPROVAL_HELP_TITLE")}
              tooltipDescription={t(
                "activity.PENDING_APPROVAL_HELP_DESCRIPTION"
              )}
            />
            <ProductModalActivity.ConfirmButton
              isVisible={!product.manager_approval}
              confirmText={t("activity.CONFIRM_COMPLETE")}
              onClick={onConfirm}
            />
          </ProductModalActivity.Bottom>
        </>
      )
    }
  }

  return <ProductModalActivity>{getContent()}</ProductModalActivity>
}

const useData = (productId: number) => {
  return useQueryAll(() => [
    custom.getProductProgress.useSuspenseQuery([
      productId,
      { apiVersion: "v1" },
    ]),

    v3.getProduct.useSuspenseQuery([productId], {
      select: (res) => res.data,
    }),

    v3.getActivityItems.useSuspenseQuery([productId], {
      select: (res) => sortBy(res.data, (item) => item.id),
    }),
  ])
}
